import * as React from "react";
import {Avatar, Popover, Segmented, Tooltip} from "antd";
import {BarChartOutlined, DashboardOutlined, DownOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, MessageOutlined} from "@ant-design/icons";
import {injectIntl} from "react-intl";
import { chooseLocale, chooseStartPage, resolveStartPage, START_PAGES } from "../../util/userPreferences";
import AppLocale from "../../lngProvider";
import { switchLanguage } from "../../appRedux/actions";
import { getStoredStartPage, getThemePreference, setThemePreference, UI_PREFERENCES_EVENT } from "../../util/uiPreferences";
import jwt_decode from "jwt-decode";
import { connect } from "react-redux";
import {useAuth0} from "../../react-auth0-spa";
import { setPermissions } from "../../appRedux/actions";
import { BULK_MESSAGING } from "../../util/permissions";
import { IntegrationType } from "../../util/integrationType";

const START_PAGE_ICONS = {
  '/my-dashboard': <DashboardOutlined/>,
  '/chat?id=&tab=2': <MessageOutlined/>,
  '/reports': <BarChartOutlined/>
};

// Short labels for the language switch; any locale registered in lngProvider shows up automatically.
const LANGUAGE_LABELS = {en: 'EN', pl: 'PL', es: 'ES'};
const LANGUAGE_NAMES = {en: 'English', pl: 'Polski', es: 'Español'};

const UserProfile = ({
  intl, initWs, hiddenHtml = false, accountName, setPermissions, permissions, locale, switchLanguage,
  collapsed = false, canCollapse = false, onToggleCollapsed
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [themePreference, setThemePreferenceState] = React.useState(getThemePreference());
  const [startPage, setStartPageState] = React.useState(resolveStartPage(getStoredStartPage()));
  React.useEffect(() => {
    const update = () => setThemePreferenceState(getThemePreference());
    window.addEventListener(UI_PREFERENCES_EVENT, update);
    return () => window.removeEventListener(UI_PREFERENCES_EVENT, update);
  }, []);
  const t = (id, defaultMessage) => intl.formatMessage({id, defaultMessage});
  const { isAuthenticated, loginWithRedirect, logout, loading, user, getTokenSilently } = useAuth0();

  if (!isAuthenticated) {
    return <button onClick={() => loginWithRedirect({})}>Log in</button>
  }

  const provider = window.sessionStorage.getItem('x-auth-provider');

    const setGlobalPermissions = permissionsParam => {
        if (!permissions) setPermissions(permissionsParam);
    };

  // TODO: this logic needs to be placed somewhere else as the code here is invoked way too many times
  if (user) {
    window.sessionStorage.setItem('sub', user.sub);
    getTokenSilently().then(jwt => {
      window.sessionStorage.setItem('jwt', jwt);
      initWs(jwt);
      const decodedToken = jwt_decode(jwt);
      const appMetadata = decodedToken['https://2waymessage.com/app_metadata'];
      setGlobalPermissions( appMetadata.permissions || []);
    });
  }
  else if ([IntegrationType.channels, IntegrationType.shoper, IntegrationType.phone_hq].includes(provider)) {
      const jwt = window.sessionStorage.getItem('jwt');
      const decodedToken = jwt_decode(jwt);
      window.sessionStorage.setItem('sub', decodedToken.user_name);
      initWs(jwt);
      setGlobalPermissions([BULK_MESSAGING]);
  } else if ([IntegrationType.livechat, IntegrationType.aws_marketplace].includes(provider)) {
      setGlobalPermissions([BULK_MESSAGING]);
  }

  const logOut = () => {
    // Communicate with mobile app
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'loggedOut'
      }));

      setTimeout(() => {
        logout({returnTo: window.origin});
      }, 3000);
    } else {
      logout({returnTo: window.origin});
    }
  };

  const hideAccountName = [IntegrationType.channels, IntegrationType.shoper, IntegrationType.phone_hq].includes(provider);
  const displayName = user?.name || user?.nickname || accountName || '';
  const initial = (displayName || '?').trim().charAt(0).toUpperCase();
  const languages = Object.keys(AppLocale);

  const changeLanguage = (value) => chooseLocale(value, switchLanguage);

  const accountMenu = (
    <div className="account-menu">
      <div className="account-menu__head">
        <div className="account-menu__name">{displayName}</div>
        <div className="account-menu__meta">
          {!hideAccountName && accountName ? <span>{t('account.name', 'Account name')}: {accountName}</span> : null}
          {user?.email ? <span>{user.email}</span> : null}
        </div>
      </div>
      {languages.length > 1 && (
        <div className="account-menu__row">
          <span>{t('sidebar.language', 'Language')}</span>
          <Segmented
            size="small"
            value={locale?.locale || 'en'}
            onChange={changeLanguage}
            options={languages.map(code => ({
              value: code,
              label: <span title={LANGUAGE_NAMES[code] || code}>{LANGUAGE_LABELS[code] || code.toUpperCase()}</span>
            }))}
          />
        </div>
      )}
      {/* Where the user lands after login: a few choices, icons with tooltips. */}
      <div className="account-menu__row">
        <span>{t('settings.startPage', 'Start page')}</span>
        <Segmented
          size="small"
          value={startPage}
          onChange={value => {
            setStartPageState(value);
            chooseStartPage(value);
          }}
          options={START_PAGES.map(page => ({
            value: page.value,
            label: (
              <Tooltip title={t(page.id, page.label)}>
                <span className="account-menu__icon-option" aria-label={t(page.id, page.label)}>{START_PAGE_ICONS[page.value]}</span>
              </Tooltip>
            )
          }))}
        />
      </div>
      <div className="account-menu__row">
        <span>{t('sidebar.theme', 'Theme')}</span>
        <Segmented
          size="small"
          value={themePreference}
          onChange={value => {
            setThemePreference(value);
            setThemePreferenceState(value);
          }}
          options={[
            {value: 'light', label: t('sidebar.themeLight', 'Light')},
            {value: 'dark', label: t('sidebar.themeDark', 'Dark')},
            {value: 'auto', label: t('sidebar.themeAuto', 'Auto')}
          ]}
        />
      </div>
      {canCollapse && (
        <button
          type="button"
          className="account-menu__row account-menu__row--button"
          onClick={() => {
            setMenuOpen(false);
            onToggleCollapsed();
          }}
        >
          <span>{collapsed ? t('sidebar.expand', 'Expand menu') : t('sidebar.collapse', 'Collapse menu')}</span>
          {collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
        </button>
      )}
      <div className="account-menu__divider"/>
      {isAuthenticated ? (
        <button type="button" className="account-menu__row account-menu__row--button account-menu__logout" onClick={() => logOut()}>
          <span>{t('sidebar.logout', 'Log out')}</span>
          <LogoutOutlined/>
        </button>
      ) : (
        <button type="button" className="account-menu__row account-menu__row--button" onClick={() => loginWithRedirect({})}>
          <span>{t('sidebar.logIn', 'Log in')}</span>
        </button>
      )}
    </div>
  );

  if (hiddenHtml) {
      return "";
  }

  const avatar = (
    <Avatar src={loading || !user?.picture ? undefined : user.picture} size={32} className="account-card__avatar" alt="">
      {initial}
    </Avatar>
  );

  return (
    <Popover
      placement={collapsed ? 'rightBottom' : 'top'}
      content={accountMenu}
      trigger="click"
      open={menuOpen}
      onOpenChange={setMenuOpen}
      overlayClassName="account-menu-popover"
      arrow={false}
    >
      <button
        type="button"
        className={`account-card ${collapsed ? 'account-card--collapsed' : ''} ${menuOpen ? 'is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={collapsed ? displayName : undefined}
      >
        {avatar}
        {!collapsed && (
          <>
            <span className="account-card__text">
              <span className="account-card__name">{displayName}</span>
              {!hideAccountName && accountName && accountName !== displayName && (
                <span className="account-card__account">{accountName}</span>
              )}
            </span>
            <DownOutlined className="account-card__chevron"/>
          </>
        )}
      </button>
    </Popover>
  );
};

const mapStateToProps = ({accounts, global, settings}) => {
    return ({
        accountName: accounts?.accountName,
        permissions: global?.permissions,
        locale: settings?.locale
    });
};

export default injectIntl(connect(mapStateToProps, {setPermissions, switchLanguage})(UserProfile));
